import { eq, and, isNull, inArray, type SQL } from 'drizzle-orm';
import { db } from '@/db/client';
import {
    recurringTransactions,
    accounts,
    creditCards,
    categories,
    transactions,
} from '@/db/schema';
import { endOfMonth, format, parse, startOfMonth } from 'date-fns';
import { parseSkippedDates } from '@/lib/recurrence';
import {
    getOccurrencesForMonth,
    monthlyAmountOfRule,
} from '@/lib/subscriptionProjection';
import { compareIso } from '@/lib/compareIso';

export type SubscriptionWithRelations = {
    id: string;
    recurringId: string | null;
    name: string;
    amount: number;
    frequency: string;
    interval: number;
    weekdays?: string | null;
    monthlyMode?: string | null;
    nextBillingDate: string;
    type: string;
    isActive: number;
    isHiddenFromSubs: number;
    /**
     * Série ignorada (ADR 0169): continua na lista (riscada, selo "Ignorada"), mas fora dos
     * totais da tela — os mesmos que alimentam o resumo da IA.
     */
    isIgnored: number;
    advanceReminderEnabled: number;
    icon?: string | null;
    color?: string | null;
    accountName?: string | null;
    creditCardName?: string | null;
    accountId?: string | null;
    creditCardId?: string | null;
    accountInstitution?: string | null;
    accountInstitutionIcon?: string | null;
    cardInstitution?: string | null;
    cardInstitutionIcon?: string | null;
    cardBrand?: string | null;
    categoryName?: string | null;
    categoryIcon?: string | null;
    categoryColor?: string | null;
};

const niceFrequencyLabels: Record<string, string> = {
    'daily:1': 'Diária',
    'weekly:1': 'Semanal',
    'weekly:2': 'Quinzenal',
    'monthly:1': 'Mensal',
    'monthly:2': 'Bimestral',
    'monthly:3': 'Trimestral',
    'monthly:6': 'Semestral',
    'annual:1': 'Anual',
};

const baseUnitLabels: Record<string, { singular: string; plural: string }> = {
    daily: { singular: 'dia', plural: 'dias' },
    weekly: { singular: 'semana', plural: 'semanas' },
    monthly: { singular: 'mês', plural: 'meses' },
    annual: { singular: 'ano', plural: 'anos' },
};

const legacyFrequencyKeys: Record<string, string> = {
    biweekly: 'weekly:2',
    bimonthly: 'monthly:2',
    quarterly: 'monthly:3',
    semiannual: 'monthly:6',
};

export function getFrequencyLabel(frequency: string, interval = 1): string {
    const key = legacyFrequencyKeys[frequency] ?? `${frequency}:${interval}`;
    if (niceFrequencyLabels[key]) {
        return niceFrequencyLabels[key];
    }

    const [unit, rawCount] = key.split(':');
    const count = Number(rawCount) || 1;
    const labels = baseUnitLabels[unit];
    if (!labels) {
        return 'Recorrente';
    }

    return `A cada ${count} ${count === 1 ? labels.singular : labels.plural}`;
}

export type RecurrenceListOptions = { includeIncome?: boolean; includePaused?: boolean };

async function querySubscriptionRules(conditions: SQL[]): Promise<SubscriptionWithRelations[]> {
    const rows = await db
        .select({
            id: recurringTransactions.id,
            recurringId: recurringTransactions.id,
            name: recurringTransactions.description,
            amount: recurringTransactions.amount,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            nextBillingDate: recurringTransactions.nextOccurrence,
            type: recurringTransactions.type,
            isActive: recurringTransactions.isActive,
            isHiddenFromSubs: recurringTransactions.isHiddenFromSubs,
            isIgnored: recurringTransactions.isIgnored,
            advanceReminderEnabled: recurringTransactions.advanceReminderEnabled,
            accountName: accounts.name,
            creditCardName: creditCards.name,
            accountId: recurringTransactions.accountId,
            creditCardId: recurringTransactions.creditCardId,
            accountInstitution: accounts.institution,
            accountInstitutionIcon: accounts.institutionIcon,
            cardInstitution: creditCards.institution,
            cardInstitutionIcon: creditCards.institutionIcon,
            cardBrand: creditCards.brand,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(creditCards, eq(recurringTransactions.creditCardId, creditCards.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .where(and(...conditions));

    return rows.map((row) => ({
        ...row,
        icon: row.categoryIcon,
        color: row.categoryColor,
    })).sort((left, right) => compareIso(left.nextBillingDate, right.nextBillingDate));
}

// Despesas ativas e visíveis: o caso comum (alimenta totais e a lista padrão).
export async function getActiveSubscriptions(): Promise<SubscriptionWithRelations[]> {
    return querySubscriptionRules([
        eq(recurringTransactions.type, 'expense'),
        eq(recurringTransactions.isActive, 1),
        eq(recurringTransactions.isHiddenFromSubs, 0),
        isNull(recurringTransactions.deletedAt),
    ]);
}

// Receitas ativas e visíveis: só pro card do topo da tela Recorrências (receita e resultado
// previsto — Helenilson, 22/09/2026). A LISTA continua opt-in via "Incluir receitas".
export async function getActiveIncomeRecurrences(): Promise<SubscriptionWithRelations[]> {
    return querySubscriptionRules([
        eq(recurringTransactions.type, 'income'),
        eq(recurringTransactions.isActive, 1),
        eq(recurringTransactions.isHiddenFromSubs, 0),
        isNull(recurringTransactions.deletedAt),
    ]);
}

// Lista da tela Recorrências: despesas ativas por padrão; receita/pausada são opt-in
// (filtro). Ocultas nunca entram aqui — vêm por getHiddenSubscriptions. Ver ADR 0018.
export async function getRecurrenceList(options: RecurrenceListOptions = {}): Promise<SubscriptionWithRelations[]> {
    const types = options.includeIncome ? ['expense', 'income'] : ['expense'];
    const statuses = options.includePaused ? [1, 0] : [1];
    return querySubscriptionRules([
        inArray(recurringTransactions.type, types),
        inArray(recurringTransactions.isActive, statuses),
        eq(recurringTransactions.isHiddenFromSubs, 0),
        isNull(recurringTransactions.deletedAt),
    ]);
}

// Recorrências ocultadas (despesa) — alimentam a seção "Ocultas" e o reexibir.
export async function getHiddenSubscriptions(): Promise<SubscriptionWithRelations[]> {
    return querySubscriptionRules([
        eq(recurringTransactions.type, 'expense'),
        eq(recurringTransactions.isHiddenFromSubs, 1),
        isNull(recurringTransactions.deletedAt),
    ]);
}

export async function hideSubscription(recurringIdOrName: string): Promise<void> {
    const now = new Date().toISOString();

    await db
        .update(recurringTransactions)
        .set({ isHiddenFromSubs: 1, updatedAt: now })
        .where(eq(recurringTransactions.id, recurringIdOrName));

    await db
        .update(transactions)
        .set({ isHiddenFromSubs: 1, updatedAt: now })
        .where(eq(transactions.recurringId, recurringIdOrName));
}

export async function hideSubscriptionByIds(recurringId: string | null, description: string): Promise<void> {
    const now = new Date().toISOString();

    if (recurringId) {
        await db
            .update(recurringTransactions)
            .set({ isHiddenFromSubs: 1, updatedAt: now })
            .where(eq(recurringTransactions.id, recurringId));

        await db
            .update(transactions)
            .set({ isHiddenFromSubs: 1, updatedAt: now })
            .where(eq(transactions.recurringId, recurringId));
        return;
    }

    await db
        .update(transactions)
        .set({ isHiddenFromSubs: 1, updatedAt: now })
        .where(eq(transactions.description, description));
}

// Reexibir: reverte o ocultar na regra E nas transações vinculadas (espelho do hide).
export async function unhideSubscription(recurringId: string): Promise<void> {
    const now = new Date().toISOString();

    await db
        .update(recurringTransactions)
        .set({ isHiddenFromSubs: 0, updatedAt: now })
        .where(eq(recurringTransactions.id, recurringId));

    await db
        .update(transactions)
        .set({ isHiddenFromSubs: 0, updatedAt: now })
        .where(eq(transactions.recurringId, recurringId));
}

/**
 * A recorrência entra nos totais da tela? A série ignorada (ADR 0169) fica listada, mas fora
 * da soma — como o Lançamento ignorado fica no extrato e fora do Balanço do mês.
 */
export function countsInRecurrenceTotals(rule: Pick<SubscriptionWithRelations, 'isIgnored'>): boolean {
    return rule.isIgnored !== 1;
}

/** Totais a partir da lista de assinaturas ativas — puro, pra UI derivar da lista que já tem. */
export function totalsOfSubscriptions(active: SubscriptionWithRelations[]): { monthly: number; annual: number } {
    const monthly = active
        .filter(countsInRecurrenceTotals)
        .reduce((sum, subscription) => sum + monthlyAmountOfRule(subscription), 0);
    return { monthly, annual: monthly * 12 };
}

export async function getTotals(): Promise<{ monthly: number; annual: number }> {
    return totalsOfSubscriptions(await getActiveSubscriptions());
}

export type CalendarEntry = SubscriptionWithRelations & { projectedDate: string };

/** Cobranças (ou entradas) do mês, do tipo pedido — o calendário segue a aba da tela. */
export async function getSubscriptionsForMonth(yearMonth: string, type: 'expense' | 'income' = 'expense'): Promise<CalendarEntry[]> {
    const monthDate = parse(yearMonth + '-01', 'yyyy-MM-dd', new Date());
    const monthStart = format(startOfMonth(monthDate), 'yyyy-MM-dd');
    const monthEnd = format(endOfMonth(monthDate), 'yyyy-MM-dd');

    const rows = await db
        .select({
            id: recurringTransactions.id,
            recurringId: recurringTransactions.id,
            name: recurringTransactions.description,
            amount: recurringTransactions.amount,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            endDate: recurringTransactions.endDate,
            skippedDates: recurringTransactions.skippedDates,
            nextBillingDate: recurringTransactions.nextOccurrence,
            type: recurringTransactions.type,
            isActive: recurringTransactions.isActive,
            isHiddenFromSubs: recurringTransactions.isHiddenFromSubs,
            isIgnored: recurringTransactions.isIgnored,
            advanceReminderEnabled: recurringTransactions.advanceReminderEnabled,
            accountName: accounts.name,
            creditCardName: creditCards.name,
            accountId: recurringTransactions.accountId,
            creditCardId: recurringTransactions.creditCardId,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(creditCards, eq(recurringTransactions.creditCardId, creditCards.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .where(
            and(
                eq(recurringTransactions.type, type),
                eq(recurringTransactions.isActive, 1),
                eq(recurringTransactions.isHiddenFromSubs, 0),
                isNull(recurringTransactions.deletedAt),
            ),
        );

    const entries: CalendarEntry[] = [];

    for (const row of rows) {
        const dates = getOccurrencesForMonth(row, monthStart, monthEnd, row.endDate, parseSkippedDates(row.skippedDates));
        for (const date of dates) {
            entries.push({
                id: row.id,
                recurringId: row.recurringId,
                name: row.name,
                amount: row.amount,
                frequency: row.frequency,
                interval: row.interval,
                weekdays: row.weekdays,
                monthlyMode: row.monthlyMode,
                nextBillingDate: row.nextBillingDate,
                type: row.type,
                isActive: row.isActive,
                isHiddenFromSubs: row.isHiddenFromSubs,
                isIgnored: row.isIgnored,
                advanceReminderEnabled: row.advanceReminderEnabled,
                icon: row.categoryIcon,
                color: row.categoryColor,
                accountName: row.accountName,
                creditCardName: row.creditCardName,
                accountId: row.accountId,
                creditCardId: row.creditCardId,
                categoryName: row.categoryName,
                categoryIcon: row.categoryIcon,
                categoryColor: row.categoryColor,
                projectedDate: date,
            });
        }
    }

    return entries.sort((left, right) => compareIso(left.projectedDate, right.projectedDate));
}
